/**
 * One screen of an agent's route tree, on whichever surface it is mounted.
 *
 * The chrome around a screen is the ONE thing that genuinely differs between
 * the frame and the sub-app (agentSurface.ts), and every depth screen was
 * hand-writing it: `h-dvh` plus a header with ReturnToMainAgent, because when
 * these screens were written the only way to reach them was full-screen.
 * In the pane both are wrong — `h-dvh` overflows the pane by the height of the
 * app header, and ReturnToMainAgent navigates out of the app the nurse is
 * already standing in.
 *
 * So the screen states what it IS (a title, a way back, its content) and this
 * decides what that means on the surface it finds itself on.
 */
import type { ReactNode } from 'react'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ReturnToMainAgent } from '@/components/ReturnToMainAgent'
import { useIsFramed } from './agentSurface'

export function AgentScreen({
  title,
  onBack,
  backLabel,
  children,
}: {
  title?: string
  onBack: () => void
  /** Optional label beside the chevron (e.g. "Back to chat" on a handoff child). */
  backLabel?: string
  children: ReactNode
}) {
  const { t } = useTranslation()
  const framed = useIsFramed()

  return (
    <div
      className={`flex flex-col bg-[var(--color-surface-bg)] ${framed ? 'h-full' : 'h-dvh'}`}
      data-agent-surface={framed ? 'frame' : 'fullscreen'}
    >
      <header
        className={`flex shrink-0 items-center gap-2 border-b border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-3 py-3 ${
          framed ? '' : 'pt-[max(0.75rem,env(safe-area-inset-top))]'
        }`}
      >
        {/* Full-screen only: in the frame the twin is the page around this. */}
        {!framed && <ReturnToMainAgent />}
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel ?? t('common.back', 'Back')}
          data-testid="agent-screen-back"
          className={`flex h-11 items-center justify-center gap-1 rounded-full text-[var(--color-surface-mute)] transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] ${
            backLabel ? 'w-auto pl-1 pr-3' : 'w-11'
          }`}
        >
          <ChevronLeft className="h-6 w-6 shrink-0" />
          {backLabel && <span className="truncate text-[15px] font-medium">{backLabel}</span>}
        </button>
        {title && (
          <h1 className="flex-1 truncate text-[17px] font-semibold text-[var(--color-surface-ink)]">
            {title}
          </h1>
        )}
      </header>
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  )
}
